import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectOption } from '../ui/select';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CreditCard, Plus, Trash2, RefreshCw } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

const PaymentStep = ({ formData, setFormData, calculateAmounts }) => {
  const [newPayment, setNewPayment] = useState({
    installment_number: '',
    installment_amount: 0,
    type: 'percentage',
    percentage: 0,
    payment_date: '',
    paid: false,
    channel: 'bank_transfer'
  });

  // const handleInputChange = (field, value) => {
  //   setFormData(prev => ({ ...prev, [field]: value }));
  // };

  // const handleNestedInputChange = (parent, field, value) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     [parent]: {
  //       ...prev[parent],
  //       [field]: value
  //     }
  //   }));
  // };

  const addPaymentInstallment = () => {
    if (!newPayment.installment_number || !newPayment.payment_date) {
      return;
    }
    
    if (newPayment.type === 'percentage' && !newPayment.percentage) {
      return;
    }
    
    if (newPayment.type === 'amount' && !newPayment.installment_amount) {
      return;
    }

    // คำนวณราคาหลังหักส่วนลด
    const discountConfig = (() => {
      try {
        const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
        return discount || {};
      } catch (e) {
        return {};
      }
    })();

    let priceAfterDiscount = formData.project_price || 0;
    
    if (discountConfig.enabled) {
      if (discountConfig.type === 'percentage') {
        const discountAmount = (priceAfterDiscount * (discountConfig.percentage || 0)) / 100;
        priceAfterDiscount = priceAfterDiscount - discountAmount;
      } else if (discountConfig.type === 'amount') {
        priceAfterDiscount = priceAfterDiscount - (discountConfig.amount || 0);
      }
    }

    const installmentAmount = newPayment.type === 'percentage' 
      ? (priceAfterDiscount * newPayment.percentage) / 100
      : newPayment.installment_amount;

    // คำนวณ WHT สำหรับงวดนี้ (คำนวณจากราคาก่อน VAT)
    const vatPercentage = (() => {
      try {
        const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
        return vat?.percentage || 0;
      } catch (e) {
        return 0;
      }
    })();
    
    const withholdingTaxEnabled = (() => {
      try {
        const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
        return withholdingTax?.enabled || false;
      } catch (e) {
        return false;
      }
    })();
    
    const withholdingTaxConfig = (() => {
      try {
        const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
        return withholdingTax || {};
      } catch (e) {
        return {};
      }
    })();
    
    // คำนวณราคาก่อน VAT สำหรับงวดนี้
    let installmentBeforeVat = 0;
    if (formData.price_type === 'include_vat') {
      // ราคารวม VAT แล้ว - แยก VAT ออก
      installmentBeforeVat = installmentAmount / (1 + (vatPercentage / 100));
    } else {
      // ราคาไม่รวม VAT
      installmentBeforeVat = installmentAmount;
    }
    
    // คำนวณ WHT ตามประเภทที่เลือก (คำนวณจากราคาก่อน VAT)
    let withholdingTaxAmount = 0;
    if (withholdingTaxEnabled) {
      if (withholdingTaxConfig.type === 'amount') {
        // ใช้จำนวนเงินที่กรอกโดยตรง
        withholdingTaxAmount = withholdingTaxConfig.amount || 0;
      } else {
        // คำนวณจากเปอร์เซ็นต์
        const withholdingTaxPercentage = withholdingTaxConfig.percentage || 0;
        withholdingTaxAmount = (installmentBeforeVat * withholdingTaxPercentage) / 100;
      }
    }

    // ยอดรับโอน = ยอดชำระ - WHT
    const receivedAmount = installmentAmount - withholdingTaxAmount;

    // คงเหลือ = ยอดรับโอน (สำหรับตอนนี้)
    const remainingAmount = receivedAmount;

    const payment = {
      ...newPayment,
      installment_amount: installmentAmount,
      withholding_tax_amount: withholdingTaxAmount,
      receivedAmount: receivedAmount,
      remainingAmount: remainingAmount,
      id: Date.now()
    };

    setFormData(prev => ({
      ...prev,
      payment_schedule: [...(prev.payment_schedule || []), payment]
    }));

    setNewPayment({
      installment_number: '',
      installment_amount: 0,
      type: 'percentage',
      percentage: 0,
      payment_date: '',
      paid: false,
      channel: 'bank_transfer'
    });
  };

  const removePaymentInstallment = (id) => {
    setFormData(prev => ({
      ...prev,
      payment_schedule: (prev.payment_schedule || []).filter(payment => payment.id !== id)
    }));
  };

  const updatePaymentInstallment = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      payment_schedule: (prev.payment_schedule || []).map(payment =>
        payment.id === id ? { ...payment, [field]: value } : payment
      )
    }));
  };

  const recalculatePaymentSchedule = () => {
    if (!formData.payment_schedule || formData.payment_schedule.length === 0) {
      return;
    }

    const amounts = calculateAmounts();
    const totalRevenue = amounts.finalAmount || 0;

    setFormData(prev => ({
      ...prev,
      payment_schedule: (prev.payment_schedule || []).map(payment => {
        let installmentAmount = 0;
        
        if (payment.type === 'percentage') {
          // คำนวณจากเปอร์เซ็นต์ของยอดรวม
          installmentAmount = (totalRevenue * payment.percentage) / 100;
        } else {
          installmentAmount = payment.installment_amount;
        }

        // คำนวณ VAT และราคาก่อน VAT สำหรับงวดนี้ (เหมือนปุ่ม + เพิ่มงวดชำระ)
        const vatConfig = (() => {
          try {
            const vat = typeof prev.vat === 'string' ? JSON.parse(prev.vat) : prev.vat;
            return vat || {};
          } catch (e) {
            return {};
          }
        })();

        const vatPercentage = vatConfig?.percentage || 0;
        // eslint-disable-next-line no-unused-vars
        const vatEnabled = vatConfig?.enabled || false;

        // คำนวณราคาก่อน VAT สำหรับงวดนี้
        let installmentBeforeVat = 0;
        if (prev.price_type === 'include_vat') {
          // ราคารวม VAT แล้ว - แยก VAT ออก
          installmentBeforeVat = installmentAmount / (1 + (vatPercentage / 100));
        } else {
          // ราคาไม่รวม VAT
          installmentBeforeVat = installmentAmount;
        }

        // คำนวณ WHT ตามประเภทที่เลือก (คำนวณจากราคาก่อน VAT)
        const withholdingTaxConfig = (() => {
          try {
            const withholdingTax = typeof prev.withholding_tax === 'string' ? JSON.parse(prev.withholding_tax) : prev.withholding_tax;
            return withholdingTax || {};
          } catch (e) {
            return {};
          }
        })();

        const withholdingTaxEnabled = withholdingTaxConfig?.enabled || false;
        let withholdingTaxAmount = 0;
        if (withholdingTaxEnabled) {
          if (withholdingTaxConfig.type === 'amount') {
            // ใช้จำนวนเงินที่กรอกโดยตรง
            withholdingTaxAmount = withholdingTaxConfig.amount || 0;
          } else {
            // คำนวณจากเปอร์เซ็นต์
            const withholdingTaxPercentage = withholdingTaxConfig.percentage || 0;
            withholdingTaxAmount = (installmentBeforeVat * withholdingTaxPercentage) / 100;
          }
        }

        // ยอดรับโอน = ยอดชำระ - WHT (เหมือนปุ่ม + เพิ่มงวดชำระ)
        const receivedAmount = installmentAmount - withholdingTaxAmount;

        // คงเหลือ = ยอดรับโอน (สำหรับตอนนี้)
        const remainingAmount = receivedAmount;

        return {
          ...payment,
          installment_amount: installmentAmount,
          receivedAmount,
          remainingAmount,
          withholding_tax_amount: withholdingTaxAmount
        };
      })
    }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-3">
          <CreditCard className="h-6 w-6 text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">การชำระเงิน</h2>
        <p className="text-muted-foreground text-sm">จัดการงวดชำระและการรับเงิน</p>
      </div>

      {/* Add New Payment */}
      <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors">
        <CardHeader className="bg-purple-50 py-3">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Plus className="h-5 w-5" />
            เพิ่มงวดชำระใหม่
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="installment_number">งวดที่</Label>
              <Input
                id="installment_number"
                type="number"
                placeholder="1"
                value={newPayment.installment_number}
                onChange={(e) => setNewPayment(prev => ({ ...prev, installment_number: e.target.value }))}
                className="h-10"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="payment_type">ประเภท</Label>
              <Select
                value={newPayment.type}
                onChange={(e) => setNewPayment(prev => ({ ...prev, type: e.target.value }))}
              >
                <SelectOption value="percentage">เปอร์เซ็นต์</SelectOption>
                <SelectOption value="amount">จำนวนเงิน</SelectOption>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_value">
                {newPayment.type === 'percentage' ? 'เปอร์เซ็นต์ (%)' : 'จำนวนเงิน'}
              </Label>
              <Input
                id="payment_value"
                type="number"
                placeholder={newPayment.type === 'percentage' ? '50' : '100000'}
                value={newPayment.type === 'percentage' ? newPayment.percentage : newPayment.installment_amount}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if (newPayment.type === 'percentage') {
                    setNewPayment(prev => ({ ...prev, percentage: value }));
                  } else {
                    setNewPayment(prev => ({ ...prev, installment_amount: value }));
                  }
                }}
                className="h-10"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_date">วันที่ชำระ</Label>
              <Input
                id="payment_date"
                type="date"
                value={newPayment.payment_date}
                onChange={(e) => setNewPayment(prev => ({ ...prev, payment_date: e.target.value }))}
                className="h-10"
              />
            </div>
          </div>

          <div className="mt-4">
            <Button
              onClick={addPaymentInstallment}
              disabled={!newPayment.installment_number || !newPayment.payment_date || 
                       (newPayment.type === 'percentage' ? !newPayment.percentage : !newPayment.installment_amount)}
              className="w-full h-10"
            >
              <Plus className="h-4 w-4 mr-2" />
              เพิ่มงวดชำระ
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Payment Schedule Table */}
      <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors">
        <CardHeader className="bg-blue-50 py-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <CreditCard className="h-5 w-5" />
              ตารางงวดชำระ
            </CardTitle>
            {formData.payment_schedule && formData.payment_schedule.length > 0 && (
              <Button
                onClick={recalculatePaymentSchedule}
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-300 hover:bg-blue-100"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                คำนวณใหม่
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {formData.payment_schedule && formData.payment_schedule.length > 0 ? (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center">งวด</TableHead>
                    <TableHead className="w-28 text-right">ยอดชำระ</TableHead>
                    <TableHead className="w-24 text-right">ส่วนลด</TableHead>
                    <TableHead className="w-20 text-right">VAT</TableHead>
                    <TableHead className="w-20 text-right">WHT</TableHead>
                    <TableHead className="w-28 text-right">ยอดรับจริง</TableHead>
                    <TableHead className="w-32">วันที่ชำระ</TableHead>
                    <TableHead className="w-24 text-center">สถานะ</TableHead>
                    <TableHead className="w-24 text-center">ช่องทาง</TableHead>
                    <TableHead className="w-16 text-center">จัดการ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(formData.payment_schedule || []).map((payment, index) => {
                    // คำนวณส่วนลดสำหรับงวดนี้
                    const discountConfig = (() => {
                      try {
                        const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                        return discount || {};
                      } catch (e) {
                        return {};
                      }
                    })();

                    let installmentDiscountAmount = 0;
                    if (discountConfig.enabled && payment.type === 'percentage') {
                      if (discountConfig.type === 'percentage') {
                        const totalDiscountAmount = (formData.project_price * discountConfig.percentage / 100);
                        installmentDiscountAmount = (totalDiscountAmount * payment.percentage / 100);
                      } else if (discountConfig.type === 'amount') {
                        installmentDiscountAmount = (discountConfig.amount * payment.percentage / 100);
                      }
                    }

                    // คำนวณ VAT สำหรับงวดนี้
                    const vatConfig = (() => {
                      try {
                        const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                        return vat || {};
                      } catch (e) {
                        return {};
                      }
                    })();

                    let installmentVatAmount = 0;
                    let installmentBeforeVat = 0;
                    
                    if (vatConfig.enabled && payment.type === 'percentage') {
                      if (formData.price_type === 'include_vat') {
                        // ราคารวม VAT แล้ว - แยก VAT ออก
                        installmentBeforeVat = payment.installment_amount / (1 + (vatConfig.percentage || 0) / 100);
                        installmentVatAmount = payment.installment_amount - installmentBeforeVat;
                      } else {
                        // ราคาไม่รวม VAT - คำนวณ VAT เพิ่ม
                        installmentBeforeVat = payment.installment_amount;
                        installmentVatAmount = (payment.installment_amount * (vatConfig.percentage || 0)) / 100;
                      }
                    } else {
                      installmentBeforeVat = payment.installment_amount;
                    }

                    return (
                      <TableRow key={payment.id || index}>
                        <TableCell className="font-medium text-center text-xs">
                          งวดที่ {payment.installment_number}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {(() => {
                            // แสดงยอดชำระตาม price_type
                            if (formData.price_type === 'include_vat') {
                              // ราคารวม VAT แล้ว - แสดงยอดชำระตามเดิม
                              return formatCurrency(payment.installment_amount);
                            } else {
                              // ราคาไม่รวม VAT - แสดงยอดชำระรวม VAT
                              const vatConfig = (() => {
                                try {
                                  const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                                  return vat || {};
                                } catch (e) {
                                  return {};
                                }
                              })();
                              
                              let installmentVatAmount = 0;
                              if (vatConfig.enabled && payment.type === 'percentage') {
                                installmentVatAmount = (payment.installment_amount * (vatConfig.percentage || 0)) / 100;
                              }
                              
                              const totalWithVat = payment.installment_amount + installmentVatAmount;
                              return formatCurrency(totalWithVat);
                            }
                          })()}
                        </TableCell>
                        <TableCell className="text-right text-xs text-yellow-600">
                          {installmentDiscountAmount > 0 ? `-${formatCurrency(installmentDiscountAmount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-xs text-green-600">
                          {installmentVatAmount > 0 ? `+${formatCurrency(installmentVatAmount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right text-xs text-orange-600">
                          {(() => {
                            // คำนวณ WHT ใหม่จาก installmentBeforeVat
                            const withholdingTaxConfig = (() => {
                              try {
                                const wht = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                                return wht || {};
                              } catch (e) {
                                return {};
                              }
                            })();
                            
                            let calculatedWhtAmount = 0;
                            if (withholdingTaxConfig.enabled) {
                              if (withholdingTaxConfig.type === 'amount') {
                                calculatedWhtAmount = withholdingTaxConfig.amount || 0;
                              } else {
                                const whtPercentage = withholdingTaxConfig.percentage || 0;
                                calculatedWhtAmount = (installmentBeforeVat * whtPercentage) / 100;
                              }
                            }
                            
                            return calculatedWhtAmount > 0 ? `-${formatCurrency(calculatedWhtAmount)}` : '-';
                          })()}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          {(() => {
                            // คำนวณยอดรับโอนใหม่ตาม price_type
                            const withholdingTaxConfig = (() => {
                              try {
                                const wht = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                                return wht || {};
                              } catch (e) {
                                return {};
                              }
                            })();
                            
                            let calculatedWhtAmount = 0;
                            if (withholdingTaxConfig.enabled) {
                              if (withholdingTaxConfig.type === 'amount') {
                                calculatedWhtAmount = withholdingTaxConfig.amount || 0;
                              } else {
                                const whtPercentage = withholdingTaxConfig.percentage || 0;
                                calculatedWhtAmount = (installmentBeforeVat * whtPercentage) / 100;
                              }
                            }
                            
                            let calculatedReceivedAmount = 0;
                            if (formData.price_type === 'include_vat') {
                              // ราคารวม VAT แล้ว - WHT
                              calculatedReceivedAmount = payment.installment_amount - calculatedWhtAmount;
                            } else {
                              // ราคาไม่รวม VAT - คำนวณ VAT เพิ่ม แล้วหัก WHT
                              const totalWithVat = payment.installment_amount + installmentVatAmount;
                              calculatedReceivedAmount = totalWithVat - calculatedWhtAmount;
                            }
                            
                            return formatCurrency(calculatedReceivedAmount);
                          })()}
                        </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={payment.payment_date || ''}
                          onChange={(e) => updatePaymentInstallment(payment.id, 'payment_date', e.target.value)}
                          className="h-7 text-xs w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={payment.paid ? 'paid' : 'pending'}
                          onChange={(e) => updatePaymentInstallment(payment.id, 'paid', e.target.value === 'paid')}
                          className="text-xs"
                        >
                          <SelectOption value="pending">รอชำระ</SelectOption>
                          <SelectOption value="paid">ชำระแล้ว</SelectOption>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={payment.channel || 'bank_transfer'}
                          onChange={(e) => updatePaymentInstallment(payment.id, 'channel', e.target.value)}
                          className="text-xs"
                        >
                          <SelectOption value="bank_transfer">โอนเงิน</SelectOption>
                          <SelectOption value="cash">เงินสด</SelectOption>
                          <SelectOption value="check">เช็ค</SelectOption>
                        </Select>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => removePaymentInstallment(payment.id)}
                          className="h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  
                  {/* Summary Row */}
                  {formData.payment_schedule && formData.payment_schedule.length > 0 && (() => {
                    // คำนวณส่วนลดรวม
                    const discountConfig = (() => {
                      try {
                        const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                        return discount || {};
                      } catch (e) {
                        return {};
                      }
                    })();

                    let totalDiscountAmount = 0;
                    if (discountConfig.enabled) {
                      if (discountConfig.type === 'percentage') {
                        totalDiscountAmount = (formData.project_price * discountConfig.percentage / 100);
                      } else if (discountConfig.type === 'amount') {
                        totalDiscountAmount = discountConfig.amount;
                      }
                    }

                    // คำนวณ VAT รวม
                    const vatConfig = (() => {
                      try {
                        const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                        return vat || {};
                      } catch (e) {
                        return {};
                      }
                    })();

                    let totalVatAmount = 0;
                    let totalWhtAmount = 0;
                    
                    if (vatConfig.enabled) {
                      if (formData.price_type === 'include_vat') {
                        // ราคารวม VAT แล้ว - แยก VAT ออก
                        const totalBeforeVat = (formData.payment_schedule || []).reduce((sum, payment) => {
                          const installmentBeforeVat = payment.installment_amount / (1 + (vatConfig.percentage || 0) / 100);
                          return sum + installmentBeforeVat;
                        }, 0);
                        totalVatAmount = (formData.payment_schedule || []).reduce((sum, payment) => sum + payment.installment_amount, 0) - totalBeforeVat;
                      } else {
                        // ราคาไม่รวม VAT - คำนวณ VAT เพิ่ม
                        totalVatAmount = ((formData.payment_schedule || []).reduce((sum, payment) => sum + payment.installment_amount, 0) * (vatConfig.percentage || 0)) / 100;
                      }
                    }
                    
                    // คำนวณ WHT รวม
                    const withholdingTaxConfig = (() => {
                      try {
                        const wht = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                        return wht || {};
                      } catch (e) {
                        return {};
                      }
                    })();
                    
                    if (withholdingTaxConfig.enabled) {
                      if (withholdingTaxConfig.type === 'amount') {
                        totalWhtAmount = withholdingTaxConfig.amount || 0;
                      } else {
                        const whtPercentage = withholdingTaxConfig.percentage || 0;
                        if (formData.price_type === 'include_vat') {
                          // คำนวณ WHT จากยอดก่อน VAT
                          const totalBeforeVat = (formData.payment_schedule || []).reduce((sum, payment) => {
                            const installmentBeforeVat = payment.installment_amount / (1 + (vatConfig.percentage || 0) / 100);
                            return sum + installmentBeforeVat;
                          }, 0);
                          totalWhtAmount = (totalBeforeVat * whtPercentage) / 100;
                        } else {
                          // คำนวณ WHT จากยอดชำระ (ไม่รวม VAT)
                          totalWhtAmount = ((formData.payment_schedule || []).reduce((sum, payment) => sum + payment.installment_amount, 0) * whtPercentage) / 100;
                        }
                      }
                    }

                    return (
                      <TableRow className="bg-gray-50 border-t-2 border-gray-200">
                        <TableCell className="text-center font-semibold text-gray-800 text-xs">
                          รวม
                        </TableCell>
                        <TableCell className="text-right font-semibold text-gray-800 text-xs">
                          {(() => {
                            // คำนวณยอดชำระรวมตาม price_type
                            const totalPaymentAmount = (formData.payment_schedule || []).reduce((sum, payment) => {
                              if (formData.price_type === 'include_vat') {
                                // ราคารวม VAT แล้ว - รวมยอดชำระตามเดิม
                                return sum + (payment.installment_amount || 0);
                              } else {
                                // ราคาไม่รวม VAT - รวมยอดชำระรวม VAT
                                const vatConfig = (() => {
                                  try {
                                    const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                                    return vat || {};
                                  } catch (e) {
                                    return {};
                                  }
                                })();
                                
                                let installmentVatAmount = 0;
                                if (vatConfig.enabled && payment.type === 'percentage') {
                                  installmentVatAmount = ((payment.installment_amount || 0) * (vatConfig.percentage || 0)) / 100;
                                }
                                
                                const totalWithVat = (payment.installment_amount || 0) + installmentVatAmount;
                                return sum + totalWithVat;
                              }
                            }, 0);
                            
                            return formatCurrency(totalPaymentAmount);
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-yellow-700 text-xs">
                          {totalDiscountAmount > 0 ? `-${formatCurrency(totalDiscountAmount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-700 text-xs">
                          {totalVatAmount > 0 ? `+${formatCurrency(totalVatAmount)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-orange-700 text-xs">
                          {formatCurrency(totalWhtAmount)}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-gray-800 text-xs">
                          {(() => {
                            // คำนวณยอดรับโอนรวมใหม่ตาม price_type
                            const totalReceivedAmount = (formData.payment_schedule || []).reduce((sum, payment) => {
                              // คำนวณ VAT สำหรับงวดนี้
                              const vatConfig = (() => {
                                try {
                                  const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                                  return vat || {};
                                } catch (e) {
                                  return {};
                                }
                              })();

                              let installmentVatAmount = 0;
                              let installmentBeforeVat = 0;
                              
                              if (vatConfig.enabled && payment.type === 'percentage') {
                                if (formData.price_type === 'include_vat') {
                                  installmentBeforeVat = payment.installment_amount / (1 + (vatConfig.percentage || 0) / 100);
                                  installmentVatAmount = payment.installment_amount - installmentBeforeVat;
                                } else {
                                  installmentBeforeVat = payment.installment_amount;
                                  installmentVatAmount = (payment.installment_amount * (vatConfig.percentage || 0)) / 100;
                                }
                              } else {
                                installmentBeforeVat = payment.installment_amount;
                              }

                              // คำนวณ WHT สำหรับงวดนี้
                              const withholdingTaxConfig = (() => {
                                try {
                                  const wht = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                                  return wht || {};
                                } catch (e) {
                                  return {};
                                }
                              })();
                              
                              let calculatedWhtAmount = 0;
                              if (withholdingTaxConfig.enabled) {
                                if (withholdingTaxConfig.type === 'amount') {
                                  calculatedWhtAmount = withholdingTaxConfig.amount || 0;
                                } else {
                                  const whtPercentage = withholdingTaxConfig.percentage || 0;
                                  calculatedWhtAmount = (installmentBeforeVat * whtPercentage) / 100;
                                }
                              }
                              
                              // คำนวณยอดรับโอนสำหรับงวดนี้
                              let calculatedReceivedAmount = 0;
                              if (formData.price_type === 'include_vat') {
                                calculatedReceivedAmount = payment.installment_amount - calculatedWhtAmount;
                              } else {
                                const totalWithVat = payment.installment_amount + installmentVatAmount;
                                calculatedReceivedAmount = totalWithVat - calculatedWhtAmount;
                              }
                              
                              return sum + calculatedReceivedAmount;
                            }, 0);
                            
                            return formatCurrency(totalReceivedAmount);
                          })()}
                        </TableCell>
                        <TableCell colSpan="4" className="text-center text-xs text-gray-600">
                          {formData.payment_schedule.length} งวด
                        </TableCell>
                      </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">ยังไม่มีงวดชำระ</p>
              <p className="text-sm text-muted-foreground">กรุณาเพิ่มงวดชำระใหม่</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentStep;
